import axios from 'axios';
import { queryParamsService } from './queryParams';
import { formatString, isValueEmpty } from './utils';
import toastService from './toastService';

const API_BASE_URL = '.'; // relative to page context
window.fetchSOWsRunning = false;

// In-flight requests, one per request kind and ticket. Gating per ticket instead of
// globally means a request still running for ticket A cannot make the identical request
// for ticket B be skipped after an in-app switch. A repeat for the same ticket is still
// skipped and resolves to undefined.
const requestsInFlight = new Set();
// The credentials are snapshotted when the request starts and handed to `request`, so a
// retry after a cookie-based 401 cannot pick up another ticket's token/issueKey if the
// technician has switched tickets in the meantime.
const runOncePerTicket = async (kind, request) => {
  const credentials = queryParamsService.getParams();
  const gate = `${kind}:${queryParamsService.getCacheKeyString(credentials.issueKey, credentials.token)}`;
  if (requestsInFlight.has(gate)) return undefined;
  requestsInFlight.add(gate);
  try {
    return await request(credentials);
  } finally {
    requestsInFlight.delete(gate);
  }
};
const eSignFetchesInFlight = new Map();

const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000,
  withCredentials: true, // send/receive the ow_session cookie
  headers: {
    'Content-Type': 'application/json',
    'X-Atlassian-Token': 'no-check'
  },
});

// Sent on every call purely so Traefik's per-technician rate-limit bucket
// (ow-ratelimit, keyed on this header) has something stable to key on. This is
// a random id, unrelated to the real SOW token — the backend never reads this
// header and auth is unaffected — generated once per (token, issueKey)
// pairing in useQueryParamsStore.
const RATE_LIMIT_ID_HEADER = 'X-OW-RateLimit-Id';

// Sent only on mutating requests that are relying on the ow_session cookie
// (i.e. the URL doesn't already carry a token= param), purely so the service
// worker (public/service-worker.js) can recover the real token if this
// request ends up offline and gets queued for background sync. It plays no
// part in authenticating the live request — that still goes through
// buildAuthQuery()/the ow_session cookie exactly as before. Needed because
// the ow_session cookie is shared per-origin across every open view: with
// several views open under different tokens, whichever view last
// authenticated overwrites that cookie for all of them, so a queued request
// replayed later via a bare fetch() would otherwise authenticate as whatever
// view is active at sync time instead of the view that queued it. Skipped
// whenever the URL already has its own token= param, since the request is
// already self-contained and sending the token again in a header would just
// be unnecessary exposure.
const OFFLINE_QUEUE_TOKEN_HEADER = 'X-OW-Offline-Token';
const MUTATING_METHODS = ['post', 'put', 'patch', 'delete'];

const isApiUrl = (url) => typeof url === 'string' && url.startsWith('/api/');
const urlHasToken = (url) => typeof url === 'string' && /[?&]token=/.test(url);

api.interceptors.request.use((config) => {
  config.headers = config.headers || {};

  const { rateLimitId, token } = queryParamsService.getParams();
  if (rateLimitId) {
    config.headers[RATE_LIMIT_ID_HEADER] = rateLimitId;
  }

  const isMutating = MUTATING_METHODS.includes(config.method?.toLowerCase());
  if (token && isMutating && isApiUrl(config.url) && !urlHasToken(config.url)) {
    config.headers[OFFLINE_QUEUE_TOKEN_HEADER] = token;
  }

  return config;
});

const RETRY_DELAY_MS = 1000;
const IDEMPOTENT_METHODS = ['get', 'head'];

// Retry-After can be delay-seconds or an HTTP-date (RFC 9110 10.2.3); falls
// back to RETRY_DELAY_MS when absent or unparseable.
const parseRetryAfterMs = (headerValue) => {
  if (!headerValue) return null;
  const seconds = Number(headerValue);
  if (!Number.isNaN(seconds)) return seconds * 1000;
  const dateMs = Date.parse(headerValue);
  return Number.isNaN(dateMs) ? null : Math.max(dateMs - Date.now(), 0);
};

// Retries once when the backend rate-limits the request. Limited to
// idempotent methods (GET/HEAD) — retrying a POST (message send, eSign, task
// completion) risks double-processing if the 429 was raised after the backend
// already partially handled it, rather than rejected up-front by Traefik.
// Doesn't cover images loaded from rendered SOW HTML (dangerouslySetInnerHTML)
// since those bypass axios — that path is handled in the service worker instead.
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const { config, response } = error;
    const method = config?.method?.toLowerCase();
    if (
      response?.status === 429 &&
      config &&
      !config._retriedAfter429 &&
      IDEMPOTENT_METHODS.includes(method)
    ) {
      config._retriedAfter429 = true;
      const delayMs = parseRetryAfterMs(response.headers?.['retry-after']) ?? RETRY_DELAY_MS;
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      return api(config);
    }
    return Promise.reject(error);
  }
);

// Non-HttpOnly companion cookie the backend sets alongside the real (HttpOnly)
// ow_session cookie, solely so the frontend can detect an active session —
// the actual session id is never readable from JS.
const SESSION_ACTIVE_COOKIE_NAME = 'ow_session_active';

const hasActiveSessionCookie = () => {
  if (typeof document === 'undefined') return false;
  return document.cookie
    .split('; ')
    .some((entry) => entry.startsWith(`${SESSION_ACTIVE_COOKIE_NAME}=`));
};

// Builds the auth query string for SOW endpoints. When the ow_session cookie
// is active, only issueKey is sent and auth relies on the cookie; otherwise
// (or when forceToken is set after a cookie-based call was rejected) the
// token is sent explicitly, matching the backend's resolveToken priority.
const buildAuthQuery = (forceToken = false, { token, issueKey } = queryParamsService.getParams()) => {
  const encodedIssueKey = encodeURIComponent(issueKey);
  return hasActiveSessionCookie() && !forceToken
    ? `issueKey=${encodedIssueKey}`
    : `token=${encodeURIComponent(token)}&issueKey=${encodedIssueKey}`;
};

// Runs an authenticated request. Falls back to sending the token explicitly
// if a cookie-based request is rejected (e.g. expired/missing session).
const authRequest = async (requestFn) => {
  try {
    return await requestFn(false);
  } catch (error) {
    if (hasActiveSessionCookie() && error?.response?.status === 401) {
      return await requestFn(true);
    }
    throw error;
  }
};

export const testConnection = async () => {
  try {
    const response = await api.get('/api/owHealth');
    return response.data;
  } catch (error) {
    const errorMessage = `API connection failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
};

export const fetchSOWDTO = () => runOncePerTicket('fetchSOWDTO', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.get(`/api/owGetSOWDTO?${buildAuthQuery(forceToken, credentials)}`)
    );
    return response.data;
  } catch (error) {
    const errorMessage = `SOW fetch failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
});

export const fetchSOWList = async () => {
  if (!window.fetchSOWsRunning) {
    window.fetchSOWsRunning = true
    try {
      const response = await authRequest((forceToken) =>
        api.get(`/api/owGetSOWList?${buildAuthQuery(forceToken)}`)
      );
      return response.data;
    } catch (error) {
      const errorMessage = `SOWs fetch failed: ${error.message}`;
      handleError(errorMessage);
      return null;
    } finally {
      window.fetchSOWsRunning = false
    }
  }
};

export const fetchSOWMessages = () => runOncePerTicket('fetchSOWMessages', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.get(`/api/owGetSOWMessages?${buildAuthQuery(forceToken, credentials)}`)
    );
    return response.data;
  } catch (error) {
    const errorMessage = `Messages fetch failed: ${error.message}`;
    handleError(errorMessage);
    return [];
  }
});

export const createSOWMessage = (messageData) => runOncePerTicket('createSOWMessage', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.post(`/api/owCreateSOWMessage?${buildAuthQuery(forceToken, credentials)}`,
        {messageData},
        {
            headers: {
              'X-Atlassian-Token': 'nocheck',
              'X-Requested-With': 'XMLHttpRequest',
              'User-Agent': 'NA'
            }
          })
    );
    return response.data;
  } catch (error) {
    const errorMessage = `Message send failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
});

export const eSignSOW = (eSignData) => runOncePerTicket('eSignSOW', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.post(`/api/owESign?${buildAuthQuery(forceToken, credentials)}`, eSignData)
    );
    return response.data;
  } catch (error) {
    const errorMessage = `Signing failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
});

export const addSOWCompletedTasks = (tasks) => runOncePerTicket('addSOWCompletedTasks', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.post(`/api/owAddSOWCompletedTasks?${buildAuthQuery(forceToken, credentials)}`, tasks)
    );
    return response.data;
  } catch (error) {
    const errorMessage = `Add SOW completed tasks failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
});

export const fetchSOWCompletedTasks = () => runOncePerTicket('fetchSOWCompletedTasks', async (credentials) => {
  try {
    const response = await authRequest((forceToken) =>
      api.get(`/api/owGetSOWCompletedTasks?${buildAuthQuery(forceToken, credentials)}`)
    );
    return response.data;
  } catch (error) {
    const errorMessage = `Fetch SOW completed tasks failed: ${error.message}`;
    handleError(errorMessage);
    return null;
  }
});

// Callers share the in-flight request for the same ticket instead of being skipped.
export const fetchSOWeSign = async () => {
  const credentials = queryParamsService.getParams();
  const ticketKey = queryParamsService.getCacheKeyString(credentials.issueKey, credentials.token);
  const inFlight = eSignFetchesInFlight.get(ticketKey);
  if (inFlight) {
    return inFlight;
  }

  const request = (async () => {
    try {
      const response = await authRequest((forceToken) =>
        api.get(`/api/owGetESign?${buildAuthQuery(forceToken, credentials)}`)
      );
      return response.data;
    } catch (error) {
      const errorMessage =
        error?.response?.data?.errorMessages?.[0] ||
        error?.response?.data?.message ||
        error?.message ||
        'Signature fetch failed';
      throw new Error(errorMessage);
    } finally {
      eSignFetchesInFlight.delete(ticketKey);
    }
  })();

  eSignFetchesInFlight.set(ticketKey, request);
  return request;
};

const handleError = (errorMessage) => {
  console.error(errorMessage);
  toastService.error(errorMessage);
}

export default api;